export type IdChangeSet = {
  add?: readonly { id?: string }[];
  edit?: readonly { id: string }[];
  remove?: readonly { id: string }[];
};

export function assertValidIdChanges(
  existing: readonly { id: string }[],
  changes: IdChangeSet | undefined,
  args: {
    operation: string;
    label: string;
  },
) {
  if (!changes) {
    return;
  }

  const existingIds = new Set(existing.map(({ id }) => id));
  const addIds = (changes.add ?? []).flatMap(({ id }) => (id ? [id] : []));
  const editIds = changes.edit?.map(({ id }) => id) ?? [];
  const removeIds = changes.remove?.map(({ id }) => id) ?? [];

  assertUniqueIds(addIds, `${args.operation} cannot add the same ${args.label} twice`);
  assertUniqueIds(editIds, `${args.operation} cannot edit the same ${args.label} twice`);
  assertUniqueIds(removeIds, `${args.operation} cannot remove the same ${args.label} twice`);

  assertNoOverlap(addIds, editIds, `${args.operation} cannot add and edit the same ${args.label}`);
  assertNoOverlap(
    addIds,
    removeIds,
    `${args.operation} cannot add and remove the same ${args.label}`,
  );
  assertNoOverlap(
    editIds,
    removeIds,
    `${args.operation} cannot edit and remove the same ${args.label}`,
  );

  for (const id of editIds) {
    if (!existingIds.has(id)) {
      throw new Error(`${args.operation} cannot edit missing ${args.label}`);
    }
  }

  for (const id of removeIds) {
    if (!existingIds.has(id)) {
      throw new Error(`${args.operation} cannot remove missing ${args.label}`);
    }
  }
}

function assertUniqueIds(ids: readonly string[], message: string) {
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) {
      throw new Error(message);
    }
    seen.add(id);
  }
}

function assertNoOverlap(left: readonly string[], right: readonly string[], message: string) {
  const rightIds = new Set(right);
  for (const id of left) {
    if (rightIds.has(id)) {
      throw new Error(message);
    }
  }
}
